import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, Subject } from 'rxjs';

// ─── Shared interfaces ────────────────────────────────────────────────────────

export interface IMediaItem {
  _id: string;
  url: string | null;
  urlSmall: string | null;
  urlMedium: string | null;
  urlLarge: string | null;
  type: 'image' | 'video';
}

export interface IUserSnippet {
  _id: string;
  displayName: string;
  userName: string;
  profileImage: { urlSmall?: string; urlMedium?: string; urlLarge?: string } | null;
  status?: string;
}

export interface ICollaboration {
  user: IUserSnippet;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED';
  invitedAt: string;
  respondedAt?: string;
}

export type ReactionType = 'LIKE' | 'HEART' | 'HUG' | 'WOW' | 'SAD' | 'HAHA';

export interface IEventInfo {
  _id: string;
  backgroundPattern?: string;   // pattern.css id e.g. "p-dots-pink"
  backgroundBg?: string;   // CSS color/gradient
  user?: string;
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  type?: 'INTERNAL' | 'EXTERNAL';
  genreOfEvent?: string;
  location?: string;
  priceMin?: string;
  priceMax?: string;
  currency?: string;
  linkToBookingSite?: string | null;
  hashtags?: string[];
  commentsDisabled?: boolean;
  mentions: IUserSnippet[];
  collaborations: ICollaboration[];
  createdAt?: string;
  updatedAt?: string;
  logo?: IMediaItem;
  __v?: number;
}

export interface IReactionSummary {
  total: number;
  counts: {
    LIKE: number;
    HEART: number;
    HUG: number;
    WOW: number;
    SAD: number;
    HAHA: number;
  };
  myReaction: ReactionType | null;

  recentUserName: string | null;
  topTypes: string[];
}

export interface IEvent {
  _id: string;
  title: string;       // caption stored here 
  type: 'EVENT';
  description?: string;
  backgroundId?: string | null;
  user: IUserSnippet;
  info: IEventInfo | null;
  media: IMediaItem[];
  hashtags: string[];
  numberOfLikes: number;
  numberOfComments: number;
  numberOfShares: number;
  engagementScore: number;
  reactions: IReactionSummary;
  __v?: number;
  isMyPostedEvent: boolean;
  isFollowed: boolean;
  friendship: { status: string; requestId?: string };
  createdAt: string;
  updatedAt: string;
}

export interface IEventsMeta {
  page: number;
  limit: number;
  total: number;
  totalMinPrice?: number;
  totalMaxPrice?: number;
}

export interface IPaginatedEvents {
  items: IEvent[];
  meta: IEventsMeta;
}

export interface IApiResponse<T> {
  success: boolean;
  statusCode: number;
  message: string;
  data: T;
}
export interface ICreatePostPayload {
  caption?: string;
  backgroundPattern?: string;
  backgroundBg?: string;
  location?: string;
  hashtags?: string;
  collaboratorIds?: string[];   // Changed to array
  mentions?: string[];   // Changed to array
  files?: File[];
}

@Injectable({
  providedIn: 'root'
})
export class EventService {

  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private baseUrl = `${environment.apiUrl}`;

  private refreshEvents$ = new Subject<void>();
  onRefreshEvents$ = this.refreshEvents$.asObservable();

  createInternalEvent(formData: FormData): Observable<HttpEvent<any>> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.post(`${this.baseUrl}/events/internal`, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }

  createExternalEvent(payload: { link: string; hashtags: string }) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.post(`${this.baseUrl}/events/external`, payload);
  }

  // ── Collaboration ──────────────────────────────────────────────────────────

  acceptCollaboration(eventId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/events/${eventId}/collaborate/accept`, {});
  }

  declineCollaboration(eventId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/events/${eventId}/collaborate/decline`, {});
  }

  /**
   * Add / update / remove reaction.
   * POST /api/v1/reactions/:contentId
   * Body: { type: "LIKE" | "HEART" | ... }
   *
   * - First call with a type  → adds reaction
   * - Same type again         → removes reaction (toggle off)
   * - Different type          → updates reaction
   */
  reactToEvent(contentId: string, type: ReactionType) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.post(`${this.baseUrl}/reactions/${contentId}`, { type });
  }

  triggerRefresh(): void {
    this.refreshEvents$.next();
  }

  getReactions(contentId: string, type?: ReactionType) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    let params = new HttpParams();
    if (type) params = params.set('type', type);
    return this.http.get(`${this.baseUrl}/reactions/${contentId}`, { params });
  }

  // NEW — trending / suggestion search
  getTrendingSearches(q: string = '') {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    let params = new HttpParams();
    if (q) params = params.set('q', q);
    return this.http.get(`${this.baseUrl}/events/trending-searches`, { params });
  }

  // UPDATED getEvents — now accepts all filter params
  getEvents(page: number, filters?: {
    priceMin?: number;
    priceMax?: number;
    location?: string;
    search?: string;
    startDate?: string;
    categories?: string;   // comma-separated
  }): Observable<IApiResponse<IPaginatedEvents>> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;

    let params = new HttpParams();
    if (filters?.priceMin !== undefined) params = params.set('priceMin', filters.priceMin);
    if (filters?.priceMax !== undefined) params = params.set('priceMax', filters.priceMax);
    if (filters?.location) params = params.set('location', filters.location);
    if (filters?.search) params = params.set('search', filters.search);
    if (filters?.startDate) params = params.set('startDate', filters.startDate);
    if (filters?.categories) params = params.set('categories', filters.categories);

    return this.http.get<IApiResponse<IPaginatedEvents>>(`${this.baseUrl}/events/${page}/list`, { params });
  }

  getRecommendedEvents(id: string) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.get(`${this.baseUrl}/events/${id}/recommendations`);
  }

  /**
   * Runs during SSR too (no isPlatformBrowser guard) — the event/:id route is
   * RenderMode.Server specifically so this data (and the SeoService tags built
   * from it) is present in the initial HTML for share-preview crawlers.
   */
  getEventDetails(id: any) {
    return this.http.get(`${this.baseUrl}/events/${id}`);
  }

  getUserEvents(userName: string, page = 1, limit = 10, collabOnly = false): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    if (collabOnly) params = params.set('collabOnly', 'true');
    return this.http.get(`${this.baseUrl}/events/user/${userName}`, { params });
  }

  getFeaturedEvents(): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.get(`${this.baseUrl}/events/featured`);
  }

  /** DELETE /api/v1/events/:eventId — owner only. */
  deleteEvent(eventId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.delete(`${this.baseUrl}/events/${eventId}`);
  }

  /** PATCH /api/v1/events/:eventId — owner only update. */
  updateEvent(eventId: string, formData: FormData): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/events/${eventId}`, formData);
  }

  /** DELETE /api/v1/events/:eventId/collaborate/leave — collaborator leaves. */
  leaveCollaboration(eventId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.delete(`${this.baseUrl}/events/${eventId}/collaborate/leave`);
  }

  /** PATCH /api/v1/events/:eventId/toggle-comments — owner only. */
  toggleComments(eventId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/events/${eventId}/toggle-comments`, {});
  }
}
